import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, GitGraph } from 'lucide-react';
import type { AppSearchResult } from './appSearchTypes';
import './AppSearchResults.css';

export interface AppSearchResultsProps {
  /** Whether the dropdown is shown at all. */
  open: boolean;
  /** Active query — used only for the empty-state copy. */
  query: string;
  results: AppSearchResult[];
  /** True while the server-side document query is in flight. */
  isLoading: boolean;
  /** Index of the highlighted row (controlled by the keyboard-nav hook). */
  activeIndex: number;
  /** Highlight a row (mouse hover). */
  onActiveIndexChange: (index: number) => void;
  /** Invoked when the user picks a result (click). */
  onSelect: (result: AppSearchResult) => void;
}

/**
 * #1392 — app-wide search results dropdown.
 *
 * Presentational only: the highlighted row is controlled and keyboard
 * navigation lives in the controller's document-level hook (focus stays
 * in the header search input after Cmd/Ctrl+F, so a div-local onKeyDown
 * here would never receive the events). Source-agnostic — each result
 * carries its own jump target, so this component knows nothing about the
 * gutschrift-check canvas or the documents feed.
 */
export const AppSearchResults: React.FC<AppSearchResultsProps> = ({
  open,
  query,
  results,
  isLoading,
  activeIndex,
  onActiveIndexChange,
  onSelect,
}) => {
  const { t } = useTranslation();

  if (!open) return null;

  const hasResults = results.length > 0;

  return (
    <div className="app-search-results" role="listbox" aria-label={t('appSearch.resultsLabel')}>
      {isLoading && (
        <div className="app-search-results__status" role="status">
          {t('appSearch.loading')}
        </div>
      )}

      {!isLoading && !hasResults && (
        <div className="app-search-results__status">
          {query.trim()
            ? t('appSearch.noResults', { query: query.trim() })
            : t('appSearch.hint')}
        </div>
      )}

      {hasResults &&
        results.map((result, idx) => (
          <button
            key={result.id}
            type="button"
            role="option"
            aria-selected={idx === activeIndex}
            className={`app-search-results__item${
              idx === activeIndex ? ' app-search-results__item--active' : ''
            }`}
            onMouseEnter={() => onActiveIndexChange(idx)}
            onClick={() => onSelect(result)}
          >
            <span className="app-search-results__icon" aria-hidden="true">
              {result.kind === 'gs-group' ? (
                <GitGraph size={16} />
              ) : (
                <FileText size={16} />
              )}
            </span>
            <span className="app-search-results__text">
              <span className="app-search-results__label">{result.label}</span>
              {result.sublabel && (
                <span className="app-search-results__sublabel">
                  {result.sublabel}
                </span>
              )}
            </span>
            <span className="app-search-results__badge">{result.badge}</span>
          </button>
        ))}
    </div>
  );
};

export default AppSearchResults;
